// New vessels on the Service sheet. Every vessel registered for service ("+ Add vessel" in Rolodex, or a
// row typed straight into Smartsheet) gets its Flag set, so the Service team sees each new job flagged; when
// someone outside the team registers it (Sales, or anyone else) the team is also told (bell + email, event
// 'service_new_vessel'), so a new job is never missed. These are the rules; the worker applies them
// (apps/worker/src/services/serviceNewVessels.ts).
//
// The sheet is GROUP rows → VESSEL rows → WORK rows. Only vessel rows (the rows right under a top-level
// group row) are ever looked at or flagged: groups and works are left alone.
/** the role and the department that make someone part of the Service team. */
export const SERVICE_TEAM_ROLE = 'service';
export const SERVICE_TEAM_DEPARTMENT = 'service';
/** on the Service team: the Service role (as the main role or an extra one), or the Service department. */
export function onServiceTeam(p) {
    const is = (want) => (x) => (x ?? '').trim().toLowerCase() === want;
    return p.roles.some(is(SERVICE_TEAM_ROLE)) || p.departments.some(is(SERVICE_TEAM_DEPARTMENT));
}
/** where a bell or email about one vessel opens: the Service page on that vessel. */
export const serviceVesselLink = (rowId) => `/service?vessel=${encodeURIComponent(String(rowId))}`;
/** the sheet's vessel rows: the rows right under a top-level (group) row, in sheet order. */
export function serviceVesselRows(rows, nameColId, flagColId) {
    const groups = new Set(rows.filter((r) => r.parentId == null).map((r) => r.id));
    const out = [];
    for (const r of rows) {
        if (r.parentId == null || !groups.has(r.parentId))
            continue;
        const name = nameColId != null ? r.cells[nameColId] : undefined;
        out.push({
            id: r.id,
            groupId: r.parentId,
            name: typeof name === 'string' ? name.trim() : '',
            flagged: flagColId != null && r.cells[flagColId] === true,
        });
    }
    return out;
}
/** after this many failed tries the app stops trying to flag a vessel (the notification still goes out). */
export const SERVICE_FLAG_MAX_ATTEMPTS = 5;
/** leeway for the clocks of two requests (or isolates) when telling whether a grid was read after a vessel
 *  was recorded. */
export const SERVICE_GRID_CLOCK_SLACK_MS = 10_000;
/** what one look at the sheet should do, from its vessel rows and the vessels already seen.
 *  A vessel without a name yet waits: it's announced once it has one. `gridReadAt` is when the grid was read
 *  from Smartsheet (null when unknown): a grid can be older than a vessel just recorded (a cached one, or a
 *  read already under way when "+ Add vessel" made it), so a vessel missing from it only counts as gone when
 *  the grid was read well after the vessel was recorded. Otherwise it's left for a later look, and the team
 *  is still told once it's named. */
export function planServiceVesselSweep(vessels, seen, baselineTaken, gridReadAt) {
    if (!baselineTaken)
        return { kind: 'baseline', rowIds: vessels.map((v) => v.id) };
    const byId = new Map(vessels.map((v) => [v.id, v]));
    const claim = [];
    const settle = [];
    const close = [];
    for (const v of vessels)
        if (!seen.has(v.id) && v.name)
            claim.push(v.id);
    for (const s of seen.values()) {
        if (!s.pending)
            continue;
        const v = byId.get(s.rowId);
        if (!v) {
            if (gridReadAt != null && s.firstSeen < gridReadAt - SERVICE_GRID_CLOCK_SLACK_MS)
                close.push(s.rowId);
            continue;
        }
        const flagDue = !s.flaggedByApp && !v.flagged && s.flagAttempts < SERVICE_FLAG_MAX_ATTEMPTS;
        const announceDue = !s.announced && !!v.name;
        if (flagDue || announceDue)
            settle.push(s.rowId);
        else if (s.announced)
            close.push(s.rowId);
    }
    return { kind: 'watch', claim, settle, close };
}
//# sourceMappingURL=serviceNewVessel.js.map